import type {
  TovChannelPlacementResponse,
  TovChannelResponse,
} from "contracts/api/client-tov/channels.responses";
import { TOV_CHANNEL_ROUTE_TYPES } from "contracts/enums/tov-voice";
import { For, Show } from "solid-js";
import { TOV_FIELD_KIND } from "../../enums/tov";
import {
  type CardEditProps,
  EditableCard,
  EditableRow,
  type FieldSpec,
} from "./EditForms";
import { CardGrid, EmptyState } from "./primitives";

const CHANNEL_FIELDS: FieldSpec[] = [
  { name: "key", label: "Key", kind: TOV_FIELD_KIND.TEXT },
  { name: "label", label: "Label", kind: TOV_FIELD_KIND.TEXT },
  {
    name: "description",
    label: "Description",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
];
const PLACEMENT_FIELDS: FieldSpec[] = [
  { name: "role", label: "Role", kind: TOV_FIELD_KIND.TEXT, grow: true },
];
const OBJECTIVE_FIELDS: FieldSpec[] = [
  {
    name: "objective",
    label: "Objective",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
];
const FORMAT_FIELDS: FieldSpec[] = [
  {
    name: "formatLabel",
    label: "Format",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
];
const METRIC_FIELDS: FieldSpec[] = [
  { name: "metricLabel", label: "Metric", kind: TOV_FIELD_KIND.TEXT },
  {
    name: "measurementContext",
    label: "Context",
    kind: TOV_FIELD_KIND.TEXT,
    optional: true,
    grow: true,
  },
];
const ROUTE_FIELDS: FieldSpec[] = [
  {
    name: "routeType",
    label: "Type",
    kind: TOV_FIELD_KIND.SELECT,
    options: TOV_CHANNEL_ROUTE_TYPES,
  },
  { name: "label", label: "Label", kind: TOV_FIELD_KIND.TEXT, grow: true },
];

// Channels tab: channel -> placements -> objectives/formats/metrics/routes. Every
// level is editable/deletable on a draft Version.
export function ChannelCards(props: {
  channels: TovChannelResponse[];
  edit: CardEditProps;
}) {
  const edit = () => props.edit;
  return (
    <section class="tov-group">
      <h2 class="tov-group-title">Channels</h2>
      <Show
        when={props.channels.length > 0}
        fallback={<EmptyState label="channels" />}
      >
        <CardGrid>
          <For each={props.channels}>
            {(channel) => (
              <EditableCard
                title={channel.label}
                editable={edit().editable}
                fields={CHANNEL_FIELDS}
                initial={{
                  key: channel.key,
                  label: channel.label,
                  description: channel.description,
                  displayOrder: channel.displayOrder,
                }}
                onSave={(values) =>
                  edit().onEdit("channels", channel.id, values)
                }
                onDelete={() => edit().onDelete("channels", channel.id)}
              >
                <p class="tov-desc">{channel.description}</p>
                <For each={channel.placements}>
                  {(placement) => (
                    <PlacementBlock placement={placement} edit={edit()} />
                  )}
                </For>
              </EditableCard>
            )}
          </For>
        </CardGrid>
      </Show>
    </section>
  );
}

function PlacementBlock(props: {
  placement: TovChannelPlacementResponse;
  edit: CardEditProps;
}) {
  const placement = () => props.placement;
  const edit = () => props.edit;
  return (
    <div class="tov-placement">
      <EditableRow
        editable={edit().editable}
        fields={PLACEMENT_FIELDS}
        initial={{
          role: placement().role,
          journeyStageId: placement().journeyStageId ?? undefined,
          displayOrder: placement().displayOrder,
        }}
        onSave={(values) => edit().onEdit("placements", placement().id, values)}
        onDelete={() => edit().onDelete("placements", placement().id)}
      >
        <span class="tov-chip">{placement().role}</span>
      </EditableRow>
      <ChildList
        label="Objectives"
        path="objectives"
        fields={OBJECTIVE_FIELDS}
        edit={edit()}
        rows={placement().objectives.map((row) => ({
          id: row.id,
          text: row.objective,
          initial: { objective: row.objective, displayOrder: row.displayOrder },
        }))}
      />
      <ChildList
        label="Formats"
        path="content-formats"
        fields={FORMAT_FIELDS}
        edit={edit()}
        rows={placement().contentFormats.map((row) => ({
          id: row.id,
          text: row.formatLabel,
          initial: {
            formatLabel: row.formatLabel,
            displayOrder: row.displayOrder,
          },
        }))}
      />
      <ChildList
        label="Metrics"
        path="metrics"
        fields={METRIC_FIELDS}
        edit={edit()}
        rows={placement().metrics.map((row) => ({
          id: row.id,
          text: row.metricLabel,
          initial: {
            metricLabel: row.metricLabel,
            measurementContext: row.measurementContext ?? undefined,
            displayOrder: row.displayOrder,
          },
        }))}
      />
      <ChildList
        label="Routes"
        path="routes"
        fields={ROUTE_FIELDS}
        edit={edit()}
        rows={placement().routes.map((row) => ({
          id: row.id,
          text: `${row.routeType}: ${row.label}`,
          initial: {
            routeType: row.routeType,
            label: row.label,
            displayOrder: row.displayOrder,
          },
        }))}
      />
    </div>
  );
}

function ChildList(props: {
  label: string;
  path: string;
  fields: FieldSpec[];
  edit: CardEditProps;
  rows: {
    id: string;
    text: string;
    initial: Record<string, string | number | undefined>;
  }[];
}) {
  return (
    <Show when={props.rows.length > 0}>
      <p class="tov-sub">
        <strong>{props.label}</strong>
      </p>
      <For each={props.rows}>
        {(row) => (
          <EditableRow
            editable={props.edit.editable}
            fields={props.fields}
            initial={row.initial}
            onSave={(values) => props.edit.onEdit(props.path, row.id, values)}
            onDelete={() => props.edit.onDelete(props.path, row.id)}
          >
            {row.text}
          </EditableRow>
        )}
      </For>
    </Show>
  );
}
